// src/components/fraud/FraudEventsTable.js
// Recent Fraud Events table widget.
// Columns: Time | User | Action | Score | Reasons | Transaction | Status
//
// UI refinements (v2):
//  • Reasons  — stacked chips/badges; "No reasons" when empty
//  • Transaction ID — shortened display, full ID in tooltip, one-click copy
//  • User UID — shortened display, full UID in tooltip, one-click copy

import React, { useState, useCallback } from 'react';
import { Container, Table } from 'react-bootstrap';
import { parseFirestoreDate } from '../../hooks/useFraudDashboard';

// ─── Action badge colours ────────────────────────────────────────────────────
const ACTION_COLOR = {
  ALLOW: '#22C55E',
  LOG: '#D9FF1F',
  REVIEW: '#F59E0B',
  BLOCK: '#EF4444',
};

const actionBadge = (action = '') => {
  const upper = action.toUpperCase();
  const color = ACTION_COLOR[upper] || 'var(--text-secondary)';
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: upper === 'LOG' ? '#050014' : color,
        background: `${color}22`,
        border: `1px solid ${color}55`,
        borderRadius: 20,
        padding: '2px 8px',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        whiteSpace: 'nowrap',
      }}
    >
      {action || '—'}
    </span>
  );
};

// ─── Score pill ──────────────────────────────────────────────────────────────
const scorePill = (score) => {
  if (score === null || score === undefined)
    return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  const color = score >= 70 ? '#EF4444' : score >= 40 ? '#F59E0B' : '#22C55E';
  return (
    <span style={{ fontWeight: 700, color, fontSize: 13 }}>{score}</span>
  );
};

// ─── Reasons chips ───────────────────────────────────────────────────────────
// Always renders; shows stacked chips for each reason or a "No reasons" label.
const ReasonChips = ({ reasons }) => {
  if (!reasons || reasons.length === 0) {
    return (
      <span style={{ color: 'var(--text-secondary)', fontSize: 11, fontStyle: 'italic' }}>
        No reasons
      </span>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {reasons.map((r, i) => (
        <span
          key={i}
          title={r}
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: '#F59E0B',
            background: 'rgba(245,158,11,0.12)',
            border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 4,
            padding: '2px 6px',
            maxWidth: 160,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {r}
        </span>
      ))}
    </div>
  );
};

// ─── Copy-to-clipboard hook ───────────────────────────────────────────────────
const useCopy = () => {
  const [copiedKey, setCopiedKey] = useState(null);
  const copy = useCallback((text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1800);
    });
  }, []);
  return { copiedKey, copy };
};

// ─── Copy button ─────────────────────────────────────────────────────────────
const CopyBtn = ({ text, copyKey, copiedKey, onCopy }) => {
  const copied = copiedKey === copyKey;
  return (
    <button
      title={copied ? 'Copied!' : `Copy: ${text}`}
      onClick={(e) => { e.stopPropagation(); onCopy(text, copyKey); }}
      style={{
        background: 'transparent',
        border: 'none',
        padding: '0 3px',
        cursor: 'pointer',
        color: copied ? '#22C55E' : 'var(--text-secondary)',
        fontSize: 11,
        lineHeight: 1,
        verticalAlign: 'middle',
        transition: 'color 0.2s',
        flexShrink: 0,
      }}
    >
      {copied ? '✓' : '⧉'}
    </button>
  );
};

// ─── Transaction ID cell ─────────────────────────────────────────────────────
// Shows a shortened ID (first 8 chars … last 5 chars) with full ID as tooltip
// and one-click copy.
const TxnCell = ({ txn, rowKey, copiedKey, onCopy }) => {
  if (!txn) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;

  const short =
    txn.length > 16
      ? `${txn.substring(0, 8)}…${txn.substring(txn.length - 5)}`
      : txn;

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <span
        title={txn}
        style={{
          fontSize: 11,
          fontFamily: 'monospace',
          color: 'var(--text-primary)',
          cursor: 'default',
          whiteSpace: 'nowrap',
        }}
      >
        {short}
      </span>
      <CopyBtn
        text={txn}
        copyKey={`txn-${rowKey}`}
        copiedKey={copiedKey}
        onCopy={onCopy}
      />
    </span>
  );
};

// ─── UID cell ────────────────────────────────────────────────────────────────
// Shows a shortened UID with full value in tooltip and one-click copy.
const UidCell = ({ user, uid, rowKey, copiedKey, onCopy }) => {
  // If user looks like an email, show it directly (it's already readable)
  const isEmail = user && user.includes('@');
  const displayUser = isEmail
    ? user
    : user && user.length > 18
    ? `${user.substring(0, 10)}…${user.substring(user.length - 5)}`
    : user || '—';

  const copyTarget = uid || user || '';
  const tooltipText = uid && uid !== user ? `UID: ${uid}\n${user}` : copyTarget;

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <span
        title={tooltipText}
        style={{
          fontSize: 12,
          color: 'var(--text-primary)',
          cursor: 'default',
          maxWidth: 150,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'inline-block',
        }}
      >
        {displayUser}
      </span>
      {copyTarget && (
        <CopyBtn
          text={copyTarget}
          copyKey={`uid-${rowKey}`}
          copiedKey={copiedKey}
          onCopy={onCopy}
        />
      )}
    </span>
  );
};

// ─── Loading skeleton rows ────────────────────────────────────────────────────
const SkeletonRows = ({ cols = 7, rows = 6 }) =>
  Array.from({ length: rows }).map((_, i) => (
    <tr key={i}>
      {Array.from({ length: cols }).map((_, j) => (
        <td key={j} className="transparent-header py-3">
          <div
            className="fraud-skeleton"
            style={{ width: j === 0 ? 80 : j === 4 ? 120 : 60, height: 12, borderRadius: 4 }}
          />
        </td>
      ))}
    </tr>
  ));

// ─── Format time ─────────────────────────────────────────────────────────────
const fmtTime = (d) => {
  if (!d) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FraudEventsTable = ({ events, loading }) => {
  const [hovered, setHovered] = useState(null);
  const { copiedKey, copy } = useCopy();

  const handleRowClick = (event) => {
    // TODO: Navigate to Fraud Log detail page in next phase
    console.log('[FraudEventsTable] Row clicked — future navigation:', event.id);
  };

  return (
    <Container fluid className="px-4 my-4">
      <div id="fraud-events-table" className="theme-card rounded-3 p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h6 className="mb-0 text-h2" style={{ fontSize: 15 }}>
            Recent Fraud Events
          </h6>
          <span
            style={{
              fontSize: 11,
              color: 'var(--text-secondary)',
              background: 'rgba(255,255,255,0.06)',
              borderRadius: 20,
              padding: '2px 10px',
            }}
          >
            Latest {loading ? '…' : events.length} events
          </span>
        </div>

        <div className="table-responsive">
          <Table className="align-middle body-text-two mb-0" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                {['Time', 'User', 'Action', 'Score', 'Reasons', 'Transaction', 'Status'].map((col) => (
                  <th
                    key={col}
                    className="transparent-header"
                    style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows />
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={7} className="transparent-header text-center py-5">
                    <div style={{ color: 'var(--text-secondary)' }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                      <p style={{ fontSize: 14 }}>
                        No fraud events found in <code>fraud_logs</code>
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                events.map((evt, idx) => (
                  <tr
                    key={evt.id || idx}
                    onClick={() => handleRowClick(evt)}
                    onMouseEnter={() => setHovered(idx)}
                    onMouseLeave={() => setHovered(null)}
                    style={{
                      borderBottom: '1px solid var(--divider)',
                      cursor: 'pointer',
                      background:
                        hovered === idx ? 'rgba(217,255,31,0.04)' : 'transparent',
                      transition: 'background 0.15s',
                    }}
                  >
                    {/* Time */}
                    <td
                      className="transparent-header py-3"
                      style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                    >
                      {fmtTime(parseFirestoreDate(evt.time) || evt.time)}
                    </td>

                    {/* User — shortened + copy UID */}
                    <td className="transparent-header py-3">
                      <UidCell
                        user={evt.user}
                        uid={evt.uid}
                        rowKey={idx}
                        copiedKey={copiedKey}
                        onCopy={copy}
                      />
                    </td>

                    {/* Action */}
                    <td className="transparent-header py-3">
                      {actionBadge(evt.action)}
                    </td>

                    {/* Score */}
                    <td className="transparent-header py-3">
                      {scorePill(evt.score)}
                    </td>

                    {/* Reasons — stacked chips, never truncated silently */}
                    <td className="transparent-header py-3">
                      <ReasonChips reasons={evt.reasons} />
                    </td>

                    {/* Transaction — shortened + copy full ID */}
                    <td className="transparent-header py-3">
                      <TxnCell
                        txn={evt.transaction}
                        rowKey={idx}
                        copiedKey={copiedKey}
                        onCopy={copy}
                      />
                    </td>

                    {/* Status */}
                    <td className="transparent-header py-3">
                      {actionBadge(evt.status)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </Container>
  );
};

export default FraudEventsTable;
